import React, { useState, useRef, useEffect } from 'react';
import { NavLink } from 'react-router-dom';
import { useSelector } from 'react-redux';
import _ from 'lodash';
import {
  ChecklistEntityIcon,
  CompendiumEntityIcon,
  KnowledgeBaseEntityIcon,
  BookmarkEntityIcon,
  ContactsEntityIcon,
  RotationsEntityIcon,
  KnowledgeHubEntityIcon,
  CommunicationCenterEntityIcon,
  EducationalEventsEntityIcon,
  HomeIcon,
  MyContentIcon,
} from 'src/images';
import { ROUTES, DATA_KEYS } from 'shared/constants';
import styles from './sidebar.module.scss';
import classnames from 'classnames';
import {
  isContactsFeatureEnabled,
  isRotationsEnabled,
  isMyContentEnabled,
  isScheduleEducationalEventsEnabled,
} from 'shared/store/selectors/features.selector';
import { getLang } from 'shared/store/selectors/lang.selector';
import {
  hasPermissionsToAccessKnowledgeHub,
  hasPermissionsToShareToKnowledgeHub,
} from 'shared/store/selectors/iam.selector';
import { isCollaborator } from 'shared/store/selectors/auth.selector';
import searchContextIdManager from 'shared/helpers/src/search-context-id-manager';
const { HOME_PAGE } = DATA_KEYS;

function MenuItem({
  path,
  title,
  icon,
  sub = [],
  exact = true,
  hide = false,
  tabIndex,
  onKeyDown,
  itemRef,
  onSelect,
  ...props
}) {
  const to = _.isArray(path) ? path[0] : path;

  if (hide) return null;

  const handleClick = () => {
    if (onSelect) {
      onSelect();
    }
  };

  // Filter out non-DOM props before spreading to NavLink
  const { hide: _hide, ...domProps } = props;

  return (
    <li>
      <NavLink
        className={({ isActive }) => `${styles.menuRoot} ${isActive ? styles.active : ''}`}
        key={path}
        to={to}
        end={exact}
        aria-label={title}
        tabIndex={tabIndex}
        onKeyDown={onKeyDown}
        ref={itemRef}
        {...domProps}
        onClick={handleClick}>
        <img src={icon} alt="" aria-hidden="true" className={styles.icon} />
        <p>{title}</p>
      </NavLink>
    </li>
  );
}

export function Sidebar() {
  const isContactsEnabled = useSelector(isContactsFeatureEnabled);
  const isRotationsEnabledState = useSelector(isRotationsEnabled);
  const isEducationalEventsVisible = useSelector(isScheduleEducationalEventsEnabled);
  const isMyContentVisible = useSelector(isMyContentEnabled);
  const isAbleToAccessKnowledgeHub = useSelector(hasPermissionsToAccessKnowledgeHub);
  const isAbleToShareToKnowledgeHub = useSelector(hasPermissionsToShareToKnowledgeHub);
  const isKnowledgeHubVisible = isAbleToAccessKnowledgeHub || isAbleToShareToKnowledgeHub;

  const lang = useSelector(getLang('SIDEBAR'));
  const schedulesLang = useSelector(getLang('SCHEDULES'));
  const hasCollaboratorPermission = useSelector(isCollaborator);

  const routes = [
    {
      path: ROUTES.HOME,
      icon: HomeIcon,
      title: lang.HOME,
    },
    {
      path: ROUTES.MY_CONTENT,
      icon: MyContentIcon,
      title: lang.MY_CONTENT,
      hide: !isMyContentVisible,
    },
    {
      path: [ROUTES.KNOWLEDGE, ROUTES.CONTENT_ITEM],
      exact: false,
      icon: KnowledgeBaseEntityIcon,
      title: lang.ROOT,
      'data-key': HOME_PAGE.KNOWLEDGE,
    },
    {
      path: ROUTES.ADMISSION,
      icon: ChecklistEntityIcon,
      exact: false,
      title: lang.ADMISSION,
    },
    {
      path: ROUTES.LABS,
      icon: CompendiumEntityIcon,
      exact: false,
      title: lang.LAB,
    },
    {
      path: ROUTES.KNOWLEDGE_HUB,
      icon: KnowledgeHubEntityIcon,
      title: lang.KNOWLEDGE_HUB,
      hide: !isKnowledgeHubVisible,
    },
    {
      path: ROUTES.CONTACTS,
      icon: ContactsEntityIcon,
      title: lang.CONTACTS,
      hide: !isContactsEnabled,
    },
    {
      path: ROUTES.COMMUNICATION_CENTER,
      icon: CommunicationCenterEntityIcon,
      title: lang.COMMUNICATION_CENTER,
      hide: !hasCollaboratorPermission,
    },
    {
      path: ROUTES.ROTATIONS,
      exact: false,
      icon: RotationsEntityIcon,
      title: lang.ROTATIONS,
      hide: !isRotationsEnabledState,
    },
    {
      path: ROUTES.FAVORITES,
      icon: BookmarkEntityIcon,
      title: lang.FAVORITES,
    },
    {
      path: ROUTES.EDUCATIONAL_EVENTS,
      exact: false,
      icon: EducationalEventsEntityIcon,
      title: schedulesLang.NAV_EDUCATIONAL_EVENTS,
      hide: !isEducationalEventsVisible,
    },
  ];

  const [focusedIndex, setFocusedIndex] = useState(-1);
  const [isExpanded, setIsExpanded] = useState(false);
  const itemRefs = useRef([]);

  // Get only visible routes for cleaner navigation logic
  const visibleRoutes = routes.filter(route => !route.hide);
  const visibleIndexToRouteIndex = visibleRoutes.map(route => routes.indexOf(route));

  useEffect(() => {
    if (focusedIndex >= 0 && focusedIndex < visibleIndexToRouteIndex.length) {
      const actualIndex = visibleIndexToRouteIndex[focusedIndex];
      if (itemRefs.current[actualIndex]) {
        itemRefs.current[actualIndex].focus();
      }
    }
  }, [focusedIndex, visibleIndexToRouteIndex]);

  const handleKeyDown = event => {
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setFocusedIndex(prev => {
        if (prev >= visibleRoutes.length - 1) {
          return 0; // Wrap to first item
        }
        return prev + 1;
      });
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setFocusedIndex(prev => {
        if (prev <= 0) {
          return visibleRoutes.length - 1; // Wrap to last item
        }
        return prev - 1;
      });
    }
  };

  const handleFocus = () => {
    setIsExpanded(true);
    // If no item is focused yet, focus the first visible item
    if (focusedIndex === -1) {
      setFocusedIndex(0);
    }
  };

  const handleBlur = event => {
    // Store reference to currentTarget before setTimeout
    const currentTarget = event.currentTarget;

    // Use setTimeout to ensure relatedTarget is available
    setTimeout(() => {
      if (currentTarget && !currentTarget.contains(document.activeElement)) {
        setIsExpanded(false);
        setFocusedIndex(-1);
      }
    }, 0);
  };

  const handleSelect = () => {
    setIsExpanded(false);
    setFocusedIndex(-1);
    searchContextIdManager.markSearchInactive(); // inactive search context on exiting from search
  };

  const handleMouseEnter = () => {
    setIsExpanded(true);
  };

  const handleMouseLeave = () => {
    // Only collapse if no item has keyboard focus
    if (focusedIndex === -1) {
      setIsExpanded(false);
    }
  };

  return (
    <nav
      className={classnames(styles.sidebar, 'new-design-system', { [styles.expanded]: isExpanded })}
      data-key={HOME_PAGE.SIDEBAR}
      onFocus={handleFocus}
      onBlur={handleBlur}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      aria-label={HOME_PAGE.SIDEBAR}>
      <ul>
        {routes.map((item, index) => {
          if (item.hide) return null;

          const visibleIndex = visibleRoutes.indexOf(item);
          const isFocused = focusedIndex === visibleIndex;

          return (
            <MenuItem
              key={item.path}
              {...item}
              tabIndex={isFocused ? 0 : -1}
              onKeyDown={handleKeyDown}
              itemRef={el => (itemRefs.current[index] = el)}
              onSelect={handleSelect}
            />
          );
        })}
      </ul>
    </nav>
  );
}
